import { PartialRecord } from '@play-co/timestep-core';
import { BlockId, blockPropsMap } from '../defs/block';
import { MapDef } from '../defs/map';

export class PuzzleMapValidator {
  constructor(
    private mapDef: MapDef,
    private legacy = false,
  ) {}

  public run(): string[] {
    return [...this.validateGoals(), ...this.validateBigBlocks()];
  }

  private validateGoals(): string[] {
    const errors: string[] = [];

    // Map out possible goals from the defined spawns and cells
    const blockMap: PartialRecord<BlockId, number> = {};
    const addBlockId = (id: BlockId) => {
      const goalId =
        blockPropsMap[id].sharedGoal ?? blockPropsMap[id].customGoal ?? id;
      const count = blockPropsMap[id].goalCountMultiplier ?? 1;

      // For obstacles that can be spawned infinitely
      const canBeSpawned = this.mapDef?.spawns.includes(id);

      // For obstacles that can generate unlimited goals
      const isGeneratorType = blockPropsMap[id].generator;

      if (!blockMap[goalId]) blockMap[goalId] = canBeSpawned ? Infinity : 0;
      if (isGeneratorType) blockMap[goalId] = Infinity;
      if (blockMap[goalId] !== Infinity) blockMap[goalId]! += count;
    };

    // Add posible blocks/goals from spawns
    this.mapDef?.spawns.forEach((id) => addBlockId(id));

    // Add posible blocks/goals from cells
    this.mapDef?.grid.cells.forEach((cell) => {
      cell.forEach(({ blocks }) => blocks?.forEach(({ id }) => addBlockId(id)));
    });
    const goalDef = this.mapDef?.goals;
    let hasGoals = false;

    // Check possible goals against defined goals
    for (const rawId in goalDef) {
      const id = rawId as BlockId;
      const achievableAmount = blockMap[id] ?? 0;
      const definedAmount = goalDef[id] ?? 0;

      if (!achievableAmount) {
        errors.push(`No goals: Goal [${id}] is not achievable.`);
      }

      if (achievableAmount < definedAmount) {
        errors.push(
          `Not enough goals: Goal [${id}] defined as ${definedAmount} but only ${achievableAmount} are achievable.`,
        );
      }

      // Do not check for extra goals in legacy mode.
      if (this.legacy) {
        continue;
      }

      if (achievableAmount !== Infinity && achievableAmount > definedAmount) {
        errors.push(
          `Extra goals: Goal [${id}] defined as ${definedAmount} but ${achievableAmount} are achievable.`,
        );
      }

      hasGoals = true;
    }

    if (!hasGoals && !this.legacy) {
      errors.push(`No goals: please define at least one goal.`);
    }

    // Do not check for missing goals in legacy mode.
    if (this.legacy) return errors;

    // Check if all goals are defined.
    const missingGoals: Record<string, number> = {};
    for (const rawId in blockMap) {
      const id = rawId as BlockId;
      const canBeSpawned = this.mapDef?.spawns.includes(id);
      if (canBeSpawned) continue;
      if (id === 'randomSpawn') continue;
      if (id === 'bomb') continue;
      if (id.startsWith('basic')) continue;
      if (id.startsWith('rocket')) continue;
      if (id.startsWith('cube')) continue;
      const def = blockPropsMap[id];
      if (def.generator || def.immortal) continue;

      const goalId = def.customGoal ?? id;
      if (!goalDef?.[goalId]) {
        missingGoals[goalId] = blockMap[id] ?? 0;
      }
    }

    for (const id in missingGoals) {
      errors.push(
        `Goal not defined: Goal [${id}] is not defined but ${missingGoals[id]} are achievable.`,
      );
    }

    return errors;
  }

  private validateBigBlocks(): string[] {
    const errors: string[] = [];

    // Get all big blocks.
    const bigBlocks: BlockId[] = Object.keys(blockPropsMap).filter((x) => {
      const block = blockPropsMap[x as BlockId];
      // Skip overlay blocks.
      if (block.overlay) return false;

      // Skip blocks that are not big.
      if ((block.width ?? 1) <= 1 && (block.height ?? 1) <= 1) return false;

      // Include the rest.
      return true;
    }) as BlockId[];

    const grid = this.mapDef!.grid!;
    const lookupPositions: { id: BlockId; x: number; y: number }[] = [];
    for (let column = 0; column < grid.columns; ++column) {
      for (let row = 0; row < grid.rows; ++row) {
        const cell = grid.cells[column][row];
        const block = cell.blocks?.find((x) => bigBlocks.includes(x.id));
        if (!block) continue;
        lookupPositions.push({ id: block.id, x: column, y: row });
      }
    }

    // Check the area around the big block.
    // If there is a block, throw an error.
    lookupPositions.forEach(({ id, x, y }) => {
      const { width, height } = blockPropsMap[id];

      // Keep track of errors already reported.
      const errorTypes: Record<string, true> = {};
      for (let column = x; column < x + width!; ++column) {
        for (let row = y; row < y + height!; ++row) {
          // Skip the big block itself
          if (column === x && row === y) continue;

          const cell = grid.cells[column][row];

          if (
            !errorTypes.blockUnder &&
            cell.blocks &&
            cell.blocks.length > 0 &&
            cell.blocks.some((x) => !blockPropsMap[x.id].overlay)
          ) {
            errorTypes.blockUnder = true;
            errors.push(
              `Block Under: Big block at (${x}, ${y}) has blocks under it`,
            );
          }
          if (!errorTypes.noCell && !cell.enabled) {
            errorTypes.noCell = true;
            errors.push(
              `No Cell Under: Big block at (${x}, ${y}) has missing cells under it`,
            );
          }
        }
      }
    });

    return errors;
  }
}
